import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { useNutritionFilterModal } from 'components/nutrition/use-filter-modal';
import { LoadingIndicator } from 'pages/store-locator/loading-indicator';
import { useErrorContext } from 'state/errors';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { primitive } from 'styles/constants/primitives';
import { INutritionExplorerWidget } from 'types/nutrition-explorer-widget';
import { scrollTo } from 'utils/scroll-to';

import { Accordion } from './accordion';
import { HORIZONTAL_PADDING_PX_DESKTOP, MAX_CONTENT_WIDTH_PX_DESKTOP } from './constants';
import { MoreOptions } from './more-options';
import { Tags } from './tags';
import { theme } from './theme';
import { IFilter, NutritionExplorerMenuObjectProductsTuple } from './types';
import { filterData, getFilterGroup, getParsedMenuData } from './utils';

const MadLibSelect = lazy(async () => ({
  default: (await import('./mad-lib-select')).MadLibSelect,
}));

const Container = styled.div`
  position: relative;
  width: 100%;

  background-color: ${p => p.theme.token('background-default')};
`;

const BlackBackground = styled.div`
  position: absolute;
  inset-inline-start: 0;
  inset-block-start: 0;
  width: 100%;
  height: 8rem;

  background-color: ${Styles.color.black};

  ${Styles.desktop} {
    height: 12rem;
  }
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  padding-block-start: ${primitive.$spacing9};

  padding-block-end: ${primitive.$spacing10};

  padding-inline: 0;
  ${Styles.desktop} {
    padding-block-start: ${primitive.$spacing9};
    padding-block-end: ${primitive.$spacing11};
    padding-inline: 0;
  }
`;

const MaxWidthContainer = styled.div`
  width: 100%;
  max-width: ${MAX_CONTENT_WIDTH_PX_DESKTOP}px;
  padding-block-start: 0;
  padding-block-end: ${primitive.$spacing4};
  padding-inline: ${primitive.$spacing3};

  ${Styles.desktop} {
    padding-block-start: 0;
    padding-block-end: ${primitive.$spacing5};
    padding-inline: ${HORIZONTAL_PADDING_PX_DESKTOP}px;
  }
`;

const FiltersContainer = styled(MaxWidthContainer)`
  width: 100%;

  display: grid;
  grid-template-areas:
    'select'
    'tags'
    'more';
  grid-template-columns: 100%;
  grid-template-rows: auto auto auto;

  padding: ${theme.filtersContainerPadding};
  background-color: ${p => p.theme.token('background-pattern')};
  border-radius: 8px;
  box-shadow: ${Styles.boxShadow};

  ${Styles.desktop} {
    grid-template-areas:
      'select more'
      'tags tags';
    grid-template-columns: 1fr min-content;
    grid-template-rows: auto auto;
    padding: ${primitive.$spacing7} ${primitive.$spacing10};
  }
`;

const MadLibSelectPlaceholder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1 1 auto;
  height: 3.625rem;

  ${Styles.desktop} {
    height: 5.125rem;
  }
`;

export const NutritionExplorerWidget: React.FC<INutritionExplorerWidget> = ({
  menu,
  categoryWhitelist,
  viewMenuButtonText,
  moreOptionsButtonText,
  madLibFilterGroup: sanityMadLibFilterGroup,
  modalFilterGroups: sanityModalFilterGroups,
}) => {
  const { setSanityDataRef } = useErrorContext();
  const enableNutritionExplorerAllergens = useFlag(
    LaunchDarklyFlag.ENABLE_NUTRITION_EXPLORER_ALLERGENS
  );

  const displayNutritionWithModifiersFromSanity = useFlag(
    LaunchDarklyFlag.DISPLAY_NUTRITION_WITH_MODIFIERS_FROM_SANITY
  );

  useEffect(() => {
    setSanityDataRef({
      data: {
        menu,
        categoryWhitelist,
        viewMenuButtonText,
        moreOptionsButtonText,
        sanityMadLibFilterGroup,
        sanityModalFilterGroups,
      },
    });
  }, [
    menu,
    categoryWhitelist,
    viewMenuButtonText,
    moreOptionsButtonText,
    sanityMadLibFilterGroup,
    sanityModalFilterGroups,
    setSanityDataRef,
  ]);
  const { formatMessage } = useIntl();
  const {
    NutritionFilterModal,
    openModal: openFilterModal,
    closeModal: closeFilterModal,
    isModalOpen: isFilterModalOpen,
  } = useNutritionFilterModal();

  const [activeMadLibFilter, setActiveMadLibFilter] = useState<IFilter | null>(null);
  const [activeModalFilters, setActiveModalFilters] = useState<IFilter[]>([]);

  const parsedData: NutritionExplorerMenuObjectProductsTuple[] = useMemo(
    () =>
      getParsedMenuData({
        menu,
        categoryWhitelist,
        enableNutritionExplorerAllergens,
        displayNutritionWithModifiersFromSanity,
      }),
    [
      menu,
      categoryWhitelist,
      enableNutritionExplorerAllergens,
      displayNutritionWithModifiersFromSanity,
    ]
  );

  const madLibFilterGroup = useMemo(
    () => (sanityMadLibFilterGroup ? getFilterGroup(sanityMadLibFilterGroup) : null),
    [sanityMadLibFilterGroup]
  );

  const modalFilterGroups = useMemo(
    () => (sanityModalFilterGroups ?? []).map(getFilterGroup),
    [sanityModalFilterGroups]
  );

  const filteredData: NutritionExplorerMenuObjectProductsTuple[] = useMemo(() => {
    const activeFilters: IFilter[] = activeModalFilters.concat(
      activeMadLibFilter ? [activeMadLibFilter] : []
    );

    return filterData(parsedData, activeFilters);
  }, [parsedData, activeModalFilters, activeMadLibFilter]);

  const productsCount = useMemo(
    () => filteredData.reduce((totalCount, [, products]) => totalCount + products.length, 0),
    [filteredData]
  );

  const onMadLibFilterChange = useCallback((newFilter: IFilter | null) => {
    setActiveMadLibFilter(newFilter);
  }, []);

  const onOpenFilterModal = useCallback(() => {
    openFilterModal();
  }, [openFilterModal]);

  useLayoutEffect(() => {
    // when showProductAccordions changes value, scroll to top
    scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  return (
    <Container>
      <BlackBackground />
      <Content>
        <MaxWidthContainer>
          <FiltersContainer>
            <Suspense
              fallback={
                <MadLibSelectPlaceholder>
                  <LoadingIndicator />
                </MadLibSelectPlaceholder>
              }
            >
              <MadLibSelect
                prompt={madLibFilterGroup?.description?.locale}
                selectedFilter={activeMadLibFilter}
                filters={madLibFilterGroup?.filters ?? []}
                onFilterChange={onMadLibFilterChange}
              />
              <MoreOptions
                openFilterModal={onOpenFilterModal}
                buttonText={moreOptionsButtonText?.locale || formatMessage({ id: 'more' })}
              />
              <Tags activeModalFilters={activeModalFilters} openFilterModal={onOpenFilterModal} />
            </Suspense>
          </FiltersContainer>
        </MaxWidthContainer>
        <MaxWidthContainer>
          {filteredData.map(([parentMenuObject, products], index) => (
            <Accordion
              parentMenuObject={parentMenuObject}
              products={products}
              startsOpen={index === 0}
              key={parentMenuObject._id}
              productBackgroundImage={menu.pickerBackgroundImage}
            />
          ))}
        </MaxWidthContainer>
      </Content>
      <NutritionFilterModal
        resultsLeft={productsCount}
        isModalOpen={isFilterModalOpen}
        filterGroups={modalFilterGroups}
        activeFilters={activeModalFilters}
        onDismiss={closeFilterModal}
        applyFilters={setActiveModalFilters}
      />
    </Container>
  );
};
